// Polyfill
import 'intersection-observer';

import Swiper from 'swiper';
import { Navigation, Pagination, EffectFade, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/effect-fade';

// Block styles loaded after Swiper css so that we can override them
import '~style/blocks/testimonials/testimonial-slider.scss';

/**
 * Intersection Observer to trigger animation when block is in viewport
 */
const options = {
  root: null,
  rootMargin: "0px 0px",
  threshold: 0.05
};

function intersectionCallback(entries) {
  entries.forEach(entry => {
    if ( entry.isIntersecting ) {
      entry.target.classList.remove('animation-standby');

      // Manually initialises Swiper
      const containerId = entry.target.getAttribute('data-container-id');
      const instance = TestimonialSlider.findInstanceById( containerId );
      if ( instance ) {
        instance.init();
      }

      observer.unobserve(entry.target);
    }
  });
}

// Registers observer
const observer = new IntersectionObserver(intersectionCallback, options);


/**
 * Testimonial Slider using Swiper js
 *
 * Read more:
 * - https://swiperjs.com/swiper-api#parameters
 */
const TestimonialSlider = {
  _swiperInstances: [],

  _counter: 0,

  _selectors: {
    container: '.js-testimonial-slider-block',
    swiper: '.js-testimonial-slider-swiper',
    prevBtn: '.js-testimonial-slider-swiper-button-prev',
    nextBtn: '.js-testimonial-slider-swiper-button-next',
    pagination: '.js-testimonial-slider-swiper-pagination'
  },

  init() {
    const self = this;

    document.querySelectorAll(this._selectors.swiper).forEach(node => {
      // Only creates instances if not already initialised
      if ( node.getAttribute('data-init') === null ) {
        self.createNewInstance(node);
      }
    });
  },

  createNewInstance(node) {
    const containerEl = node.closest( this._selectors.container );

    // Generates unique container id
    const containerId = 'swiper-inst-' + this._counter;
    this._counter ++;

    // Adds id to container element
    containerEl.setAttribute('data-container-id', containerId);

    // Used to specify selectors within this container only
    const containerSelector = this._selectors.container + '[data-container-id="'+ containerId +'"]';

    /**
     * Swiper instance
     */
    const instance = new Swiper(node, {
      modules: [ Navigation, Pagination, EffectFade, Autoplay ],
      init: false, // swiper needs to be manually initialised via .init() method
      autoplay: {
        delay: 4000,
      },
      speed: 400,
      effect: 'fade',
      fadeEffect: {
        crossFade: true
      },
      loop: true,
      autoHeight: false,
      navigation: {
        nextEl: containerSelector + ' ' + this._selectors.nextBtn,
        prevEl: containerSelector + ' ' + this._selectors.prevBtn,
      },
      pagination: {
        el: containerSelector + ' ' + this._selectors.pagination,
        type: 'fraction',
        formatFractionCurrent: function (number) {
          return ('0' + number).slice(-2);
        },
        formatFractionTotal: function (number) {
          return ('0' + number).slice(-2);
        },
        renderFraction: function (currentClass, totalClass) {
          return '<span class="' + currentClass + '"></span><span class="fraction-symbol">-</span><span class="' + totalClass + '"></span>';
        }
      }
    });

    // Stores instance
    this._swiperInstances.push({
      containerId,
      instance
    });

    // Marks container as initialised to prevent duplicate initialisations
    containerEl.setAttribute('data-init', '1');

    // Adds observer to container so that we can trigger animation when it appears in the viewport
    observer.observe( containerEl );
  },

  findInstanceById( containerId ) {
    const entry = this._swiperInstances.find(instance => instance.containerId === containerId );

    if ( typeof entry === 'undefined' ) {
      return null;
    }

    return entry.instance;
  }
};

export default TestimonialSlider;
